import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { getCurrentSelectedTenant } from "sj-ui-toolkit/src/utils";
import { humanDateWithoutTime } from "sj-ui-toolkit/src/utils/dates";
import { AppContext } from "@/context/AppContext";
import { loadAssignmentPolicyByIdAsync, loadAssignmentPolicySummaryById } from "@/api/assignments";
import type { AssignmentPolicyData } from "@/api/assignments";
import {
  normalizeNotificationsFrequency,
  parseAssignmentProgress,
  reminderTextMap,
  capitalize,
} from "@/helpers/assignmentHelpers";
import { useFeatureFlagGate } from "@/hooks/useFeatureFlagGate";
import { AssignmentProgressChart } from "@/components/assignments/AssignmentProgressChart";
import { AssignmentUsersTable } from "@/components/assignments/AssignmentUsersTable";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const NO_DUE_DATE = "0001-01-01T00:00:00Z";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const AssignmentDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { state } = useContext(AppContext);
  const { viewOnly } = state;
  const { adminLandingPages } = useFlags();
  const gate = useFeatureFlagGate(adminLandingPages);

  const [policy, setPolicy] = useState<AssignmentPolicyData | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadNonce, setReloadNonce] = useState(0);

  // showToast identity isn't stable, so keep it out of effect deps via a ref.
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  useBreadcrumb(
    policy
      ? {
          label: policy.name ?? "Assignment",
          route: AdminAppRoutes.routes.assignments.routes.id.route.replace(":id", id ?? ""),
          display: "page",
        }
      : null
  );

  useEffect(() => {
    // Flag off/absent — restore the pre-feature behavior of opening the edit form.
    if (gate === "disabled" && id) {
      navigate(AdminAppRoutes.routes.assignments.routes.edit.route.replace(":id", id), {
        replace: true,
      });
    }
  }, [gate, id, navigate]);

  useEffect(() => {
    if (gate !== "enabled" || !id) return;
    let cancelled = false;

    setPolicy(null);
    setLoadFailed(false);
    const tenantName = getCurrentSelectedTenant() ?? "";

    // The Rails single-record endpoint has no `progress` field — pull it (and the
    // other svc-sourced fields) from the same svc endpoint the assignments table uses,
    // scoped by name server-side instead of paging through the whole tenant.
    loadAssignmentPolicyByIdAsync(id)
      .then(async detail => {
        if (cancelled) return;
        const summary = detail.name
          ? await loadAssignmentPolicySummaryById(tenantName, id, detail.name)
          : undefined;
        if (cancelled) return;
        setPolicy({ ...detail, ...summary });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("Failed to load assignment:", error);
        showToastRef.current({ severity: "error", detail: "Failed to load assignment" });
        setLoadFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [gate, id, reloadNonce]);

  if (!id) return null;

  // Spinner while LD resolves the flag, and while a disabled gate redirects away.
  if (gate !== "enabled") {
    return (
      <div className="flex justify-center py-8">
        <ProgressSpinner />
      </div>
    );
  }

  if (loadFailed) {
    return (
      <div className="flex flex-col items-start gap-4 py-8">
        <Button
          label="Back to Assignments"
          icon="pi pi-arrow-left"
          text
          onClick={() => navigate(AdminAppRoutes.routes.assignments.route)}
        />
        <Message severity="error" text="We couldn't load this assignment." />
        <Button
          label="Try again"
          icon="pi pi-refresh"
          data-testid="assignment-details-retry-button"
          onClick={() => setReloadNonce(n => n + 1)}
        />
      </div>
    );
  }

  if (!policy) {
    return (
      <div className="flex justify-center py-8">
        <ProgressSpinner />
      </div>
    );
  }

  const dueDateLabel =
    policy.dueDateAt && policy.dueDateAt !== NO_DUE_DATE
      ? humanDateWithoutTime(policy.dueDateAt)
      : "No due date";
  const cadenceKey = capitalize(normalizeNotificationsFrequency(policy.notificationsFrequency));
  const progress = parseAssignmentProgress(policy.progress);

  return (
    <div className="flex flex-col gap-4">
      {/* Status tag + action buttons */}
      <div className="flex flex-wrap items-center justify-between gap-8">
        <Tag
          value={policy.activated ? "ACTIVE" : "INACTIVE"}
          severity={policy.activated ? "success" : "warning"}
          rounded
        />
        {!viewOnly && (
          <Button
            label="Edit"
            icon="pi pi-pencil"
            severity="secondary"
            data-testid="assignment-details-edit-button"
            onClick={() =>
              navigate(AdminAppRoutes.routes.assignments.routes.edit.route.replace(":id", id ?? ""))
            }
          />
        )}
      </div>

      {/* Title + description */}
      <div>
        <h1 className="atlas-h2-alt leading-tight">{policy.name}</h1>
        <p className="body-lg max-w-xl">{policy.description}</p>
      </div>

      {/* Chart + stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-6 mb-6">
        <div>
          <h2 className="atlas-h5 mb-4">Progress</h2>
          <AssignmentProgressChart progress={progress} />
        </div>
        <div className="grid grid-cols-2 gap-8">
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Due Date</h3>
            <p className="atlas-h2">{dueDateLabel}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Notification Cadence</h3>
            <p className="atlas-h2">{cadenceKey}</p>
            <p className="text-secondary body-sm">{reminderTextMap[cadenceKey]}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Audiences</h3>
            <p className="atlas-h2">{policy.audiences?.map(a => a.name).join(", ") || "\u2014"}</p>
          </div>
          <div>
            <h3 className="atlas-h5 text-color-secondary mb-2">Total Assigned</h3>
            <p className="atlas-h2">{progress.total}</p>
          </div>
        </div>
      </div>

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="atlas-h5">Assigned Users</h2>
          <Button
            label="View Progress Report"
            icon="pi pi-chart-bar"
            severity="secondary"
            outlined
            data-testid="assignment-details-progress-report-link"
            onClick={() =>
              navigate(
                `${AdminAppRoutes.routes.reporting.routes.assignments.route}?assignment=${encodeURIComponent(policy.name ?? "")}`
              )
            }
          />
        </div>
        <AssignmentUsersTable assignmentName={policy.name ?? ""} />
      </Card>
    </div>
  );
};

export default AssignmentDetails;
